import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const PUBLIC_ROUTES = [
  "/",
  "/about",
  "/contact",
  "/pricing",
  "/privacy-policy",
  "/terms",
  "/blog",
  "/thank-you",
  "/register-success",
];

const STORAGE_KEY = "cookie_consent";

export function CookieConsentBanner() {
  const location = useLocation();
  const [visible, setVisible] = useState(false);

  const isPublicRoute =
    PUBLIC_ROUTES.includes(location.pathname) ||
    location.pathname.startsWith("/blog/");

  useEffect(() => {
    if (!isPublicRoute) return;
    const consent = localStorage.getItem(STORAGE_KEY);
    if (!consent) {
      // Small delay so page content renders first
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, [isPublicRoute]);

  const handleAccept = () => {
    localStorage.setItem(STORAGE_KEY, "accepted");
    setVisible(false);
  };

  const handleDecline = () => {
    localStorage.setItem(STORAGE_KEY, "declined");
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      className={cn(
        "fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6",
        "animate-in slide-in-from-bottom-4 duration-500"
      )}
    >
      <div className="mx-auto max-w-4xl bg-card border border-border rounded-xl shadow-2xl p-4 md:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
        {/* Icon */}
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
          <Cookie className="w-5 h-5 text-primary" />
        </div>

        {/* Text */}
        <div className="flex-1 text-sm text-muted-foreground">
          <p className="font-semibold text-foreground mb-0.5">
            We use cookies 🍪
          </p>
          <p>
            We use third-party advertising companies (like{" "}
            <strong className="text-foreground">Google AdSense</strong>) and
            analytics tools to serve ads and improve your experience. These
            companies may use cookies based on your prior visits to this or
            other websites.{" "}
            <a
              href="/privacy-policy"
              className="underline underline-offset-2 hover:text-primary transition-colors"
            >
              Learn more about our privacy policy
            </a>
            .
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto">
          <Button
            size="sm"
            variant="outline"
            onClick={handleDecline}
            className="flex-1 sm:flex-none"
          >
            Decline
          </Button>
          <Button
            size="sm"
            onClick={handleAccept}
            className="flex-1 sm:flex-none"
          >
            Accept All
          </Button>
          <button
            onClick={handleDecline}
            aria-label="Close"
            className="ml-1 text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
